import React, { createContext, useState, useEffect, useContext, useCallback } from 'react'; // Added useCallback
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { loginUser, registerUser, refreshTokenRequest, authenticatedRequest, logoutUser } from '../services/authService'; // Import logoutUser

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // --- Helper function to set user from token ---
  const setUserFromToken = (accessToken) => {
    const decoded = jwtDecode(accessToken);
    
    setUser({
      id: decoded.user_id,
      username: decoded.username,
      isAdmin: decoded.is_staff,
      isSuperuser: decoded.is_superuser,
    });
  };

  // --- Logout Function ---
  // Use useCallback to prevent unnecessary re-renders if passed as dependency
  const logout = useCallback(async () => {
    const refreshTokenToBlacklist = localStorage.getItem('refreshToken');
    
    try {
      if (refreshTokenToBlacklist) {
        console.log("Attempting backend logout...");
        await logoutUser(refreshTokenToBlacklist); // Attempt backend logout FIRST
      } else {
        console.warn("No refresh token found in local storage to blacklist.");
      }
    } catch (error) {
      // Log the error from backend logout attempt, but proceed
      console.error("Backend logout API call failed:", error); 
    } finally {
      // ALWAYS clear frontend state and navigate, regardless of backend success/failure
      console.log("Clearing frontend state and navigating to login.");
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      navigate('/login'); 
    }
  }, [navigate]); // Dependency on navigate

  // --- Refresh Token Function ---
  // Use useCallback
  const refreshToken = useCallback(async () => {
    const currentRefreshToken = localStorage.getItem('refreshToken');
    if (!currentRefreshToken) {
      console.log("No refresh token found, logging out.");
      logout(); // Call logout directly
      throw new Error('No refresh token');
    }

    try {
      console.log("Attempting token refresh...");
      const data = await refreshTokenRequest(currentRefreshToken); // Use service
      localStorage.setItem('accessToken', data.access);
      setUserFromToken(data.access); // Use helper
      console.log("Token refreshed successfully.");
      return data.access;
    } catch (error) {
      console.error('Refresh token failed:', error);
      logout(); // Call logout on failure
      throw error; // Re-throw error to be caught by caller if needed
    }
  }, [logout]); // Include logout in dependency array
  
  // --- Initial Load/Token Check ---
  useEffect(() => {
    console.log("Auth Provider mounted. Checking token...");
    const checkToken = async () => {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        try {
          const decoded = jwtDecode(accessToken);
          const currentTime = Date.now() / 1000;

          if (decoded.exp < currentTime) {
            console.log("Access token expired, attempting refresh...");
            await refreshToken(); // Refresh token will set user if successful
          } else {
            console.log("Access token valid, setting user.");
            setUserFromToken(accessToken); // Use helper
          }
        } catch (error) {
          // This catches errors from jwtDecode (invalid token) or refreshToken
          console.error("Error during initial token check:", error);
          logout(); // Logout if token is invalid or refresh fails
        }
      } else {
        console.log("No access token found.");
        // No token, user is not logged in
      }
      setLoading(false); // Ensure loading is set to false in all cases
    };

    checkToken();
  }, [refreshToken, logout]); // Dependencies


// --- Login Function ---
const login = async (username, password, userType = 'user') => {
  setLoading(true);
  try {
    const data = await loginUser(username, password, userType); // Use service with userType
    localStorage.setItem('accessToken', data.access);
    localStorage.setItem('refreshToken', data.refresh);
    localStorage.setItem('user', JSON.stringify(data.user));
    
    // First set the user from token
    setUserFromToken(data.access); // Use helper
    
    // Navigate based on user type and privileges
    if (userType === 'superadmin') {
      if (data.user.is_superuser) {
        navigate('/superadmin/dashboard');
      } else {
        // This shouldn't happen due to backend validation, but just in case
        localStorage.clear();
        setUser(null);
        setLoading(false);
        throw new Error('Access denied. Superadmin privileges required.');
      }
    } else {
      // Regular user login
      if (!data.user.is_superuser) {
        // navigate('/admin_dashboard');
        navigate('/dashboard');
      } else {
        navigate('/login')
      }
    }
    
    setLoading(false);
    return true;
  } catch (error) {
    console.error('Login failed in context:', error);
    // Clear any potentially partially set tokens on failure
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setLoading(false);
    // Re-throw the error so the component can access the actual error message
    throw error;
  }
};
  
  // --- Register Function ---
  const register = async (userData) => {
    setLoading(true);
    try {
      await registerUser(userData); // Use service
      // Automatically login after successful registration
      const loginSuccess = await login(userData.username, userData.password);
      setLoading(false);
      return loginSuccess; // Return the result of the login attempt
    } catch (error) {
      console.error('Registration failed in context:', error);
      setLoading(false);
      throw error; // Re-throw the error to be handled by the component
    }
  };

  
  // --- Context Value ---
  const value = {
    user,
    loading,
    login,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};